import { useEffect, useMemo, useState } from "react";
import { useSnapshot } from "../contexts/SnapshotContext";
import {
  listObjetivos,
  createObjetivo,
  updateObjetivo,
  deleteObjetivo,
  fetchConsultores,
} from "../lib/api";
import { formatEUR, formatNum } from "../lib/format";
import { RankBadge } from "../components/RankBadge";
import { Target, Plus, Trash, PencilSimple, CheckCircle, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const METRICS = [
  { value: "vendas_pessoais", label: "Ventas personales (€)", unit: "€" },
  { value: "vendas_equipa", label: "Ventas equipo (€)", unit: "€" },
  { value: "diretos_ativos", label: "Directas activas", unit: "" },
  { value: "altas_personales", label: "Altas personales (directas)", unit: "" },
  { value: "altas_equipo", label: "Altas de equipo (toda la línea)", unit: "" },
  { value: "junior_lideres_equipa", label: "Júnior en equipo", unit: "" },
];

function ProgressBar({ value, met }) {
  const c = met ? "bg-[#8A9A86]" : value >= 60 ? "bg-[#E3A569]" : "bg-[#C57B67]";
  return (
    <div className="h-2 w-full bg-[#F2EFEA] rounded-full overflow-hidden">
      <div className={`h-full ${c}`} style={{ width: `${Math.min(100, value)}%` }} />
    </div>
  );
}

export default function Objetivos() {
  const { selected } = useSnapshot();
  const [items, setItems] = useState([]);
  const [consultoras, setConsultoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);

  const load = async () => {
    if (!selected) {
      setItems([]);
      setConsultoras([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const period = selected.period;
    const [r, cs] = await Promise.all([
      listObjetivos({ period, snapshot_id: selected.id }),
      fetchConsultores({ snapshot_id: selected.id }),
    ]);
    setItems(r.items || []);
    setConsultoras(cs.items || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCreate = () => {
    const period = selected ? selected.period : `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
    setForm({
      period,
      id_consultor: "",
      metric: "vendas_pessoais",
      target: 200,
      note: "",
    });
    setCreating(true);
  };

  const submit = async () => {
    if (!form.id_consultor || !form.target) {
      toast.error("Selecciona consultora y objetivo");
      return;
    }
    try {
      await createObjetivo({ ...form, id_consultor: Number(form.id_consultor), target: Number(form.target) });
      toast.success("Objetivo creado");
      setCreating(false);
      load();
    } catch {
      toast.error("Error al crear");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este objetivo?")) return;
    await deleteObjetivo(id);
    toast.success("Objetivo eliminado");
    load();
  };

  const stats = useMemo(() => {
    const cumplidos = items.filter((o) => o.met).length;
    return { total: items.length, cumplidos, pendientes: items.length - cumplidos };
  }, [items]);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="objetivos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs tracking-widest uppercase text-[#736F68] font-display font-semibold">
            Objetivos del mes
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-semibold font-display text-[#2D2A26]">
            Mis objetivos
          </h1>
          <p className="mt-1 text-[#736F68] text-sm">
            Marca metas individuales por consultora y observa el progreso en tiempo real.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C57B67] hover:bg-[#B06A57] text-white text-sm font-medium"
          data-testid="create-objetivo-btn"
        >
          <Plus size={16} weight="bold" />
          Nuevo objetivo
        </button>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total" value={stats.total} accent="#2D2A26" />
        <StatCard label="Cumplidos" value={stats.cumplidos} accent="#8A9A86" />
        <StatCard label="Pendientes" value={stats.pendientes} accent="#E3A569" />
      </section>

      {loading ? (
        <p className="text-[#736F68]">Cargando...</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-10 text-center">
          <Target size={40} weight="duotone" className="mx-auto text-[#C57B67]" />
          <p className="mt-3 font-semibold text-[#2D2A26]">Aún no has fijado objetivos</p>
          <p className="text-sm text-[#736F68] mt-1">
            Crea metas individuales para empujar a cada consultora.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((o) => {
            const m = METRICS.find((mm) => mm.value === o.metric);
            return (
              <div
                key={o.id}
                className="bg-white border border-[#E5E0D8] rounded-xl p-5 flex items-center gap-4"
                data-testid={`objetivo-${o.id}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#F2EFEA] flex items-center justify-center">
                  {o.met ? (
                    <CheckCircle size={22} weight="fill" className="text-[#8A9A86]" />
                  ) : (
                    <Target size={22} weight="duotone" className="text-[#C57B67]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-[#2D2A26]">{o.nome || `ID ${o.id_consultor}`}</p>
                    <span className="text-xs text-[#736F68]">{o.metric_label}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="flex-1 max-w-md">
                      <ProgressBar value={o.progress} met={o.met} />
                    </div>
                    <p className="text-xs font-mono text-[#736F68] whitespace-nowrap">
                      {m?.unit === "€" ? formatEUR(o.current) : formatNum(o.current)} /{" "}
                      {m?.unit === "€" ? formatEUR(o.target) : formatNum(o.target)} ·{" "}
                      <span className={o.met ? "text-[#8A9A86] font-semibold" : ""}>{o.progress}%</span>
                    </p>
                  </div>
                  {o.note && <p className="text-xs text-[#736F68] mt-1 italic">"{o.note}"</p>}
                </div>
                <button
                  onClick={() => handleDelete(o.id)}
                  className="p-2 rounded-lg text-[#736F68] hover:text-[#B85C5C] hover:bg-[#B85C5C]/10"
                >
                  <Trash size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent data-testid="create-objetivo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Nuevo objetivo</DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#736F68]">Consultora</Label>
                <Select value={String(form.id_consultor)} onValueChange={(v) => setForm({ ...form, id_consultor: v })}>
                  <SelectTrigger data-testid="objetivo-consultora">
                    <SelectValue placeholder="Selecciona consultora" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {consultoras.map((c) => (
                      <SelectItem key={c.id_consultor} value={String(c.id_consultor)}>
                        {c.nome} · {c.titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#736F68]">Métrica</Label>
                  <Select value={form.metric} onValueChange={(v) => setForm({ ...form, metric: v })}>
                    <SelectTrigger data-testid="objetivo-metric">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METRICS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#736F68]">Objetivo (≥)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.target}
                    onChange={(e) => setForm({ ...form, target: e.target.value })}
                    data-testid="objetivo-target"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#736F68]">Nota (opcional)</Label>
                <Input
                  placeholder="Ej. Empuje final del mes"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancelar
            </Button>
            <Button onClick={submit} className="bg-[#C57B67] hover:bg-[#B06A57] text-white" data-testid="save-objetivo-btn">
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-xl p-4">
      <p className="text-[10px] uppercase tracking-wider text-[#736F68] font-semibold">{label}</p>
      <p className="mt-2 text-3xl font-semibold font-display" style={{ color: accent }}>
        {value}
      </p>
    </div>
  );
}